import type { AdminDashboardGetQuery } from '@/graphql/generated/sdk'

export interface DashboardAlert {
    kind: string
    severity: string
    relatedId: number | null
    relatedType: string
    relatedName: string
}

export interface DashboardEvent {
    id: number
    name: string
    startAt: string | null
    endAt: string | null
    ticketsTotal: number
    ticketsUsed: number
    scanRateLastHour: number
}

export interface DashboardScanLog {
    id: string
    createdAt: string
    deviceName: string | null
    ticketCode: string
    ticketsetName: string
    eventName: string
}

export interface DashboardInstance {
    id: number
    name: string
    activeEventsCount: number
    scansLast24h: number
    devicesOnline: number
    devicesTotal: number
}

export interface DashboardData {
    validationsLast24h: number | null
    validationsDelta: number | null
    hourlyValidations: number[] | null
    ticketsTotal: number | null
    ticketsUsed: number | null
    ticketsUsagePercent: number | null
    activeDevicesCount: number | null
    totalDevicesCount: number | null
    upcomingEventsCount: number | null
    activeEvents: DashboardEvent[] | null
    recentScans: DashboardScanLog[] | null
    alerts: DashboardAlert[] | null
    instances: DashboardInstance[] | null
}

export function computeDelta(curr: number | null, prev: number | null): number | null {
    if (curr === null || prev === null) return null
    if (prev === 0) return curr > 0 ? 100 : 0
    return ((curr - prev) / prev) * 100
}

const SEVERITY_RANK: Record<string, number> = { DANGER: 2, WARNING: 1 }

export function sortAlerts(alerts: DashboardAlert[] | null): DashboardAlert[] | null {
    if (alerts === null) return null
    return [...alerts].sort((a, b) => (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0))
}

export function processDashboard(
    raw: NonNullable<AdminDashboardGetQuery['admin']>['dashboard']
): DashboardData {
    const get = raw?.get
    const ticketsTotal = get?.ticketsTotal ?? null
    const ticketsUsed = get?.ticketsUsed ?? null
    const ticketsUsagePercent =
        ticketsTotal !== null && ticketsUsed !== null && ticketsTotal > 0
            ? Math.round((ticketsUsed / ticketsTotal) * 100)
            : null

    return {
        validationsLast24h: get?.validationsLast24h ?? null,
        validationsDelta: computeDelta(get?.validationsLast24h ?? null, get?.validationsPrev24h ?? null),
        hourlyValidations: (get?.hourlyValidations ?? null) as number[] | null,
        ticketsTotal,
        ticketsUsed,
        ticketsUsagePercent,
        activeDevicesCount: get?.activeDevicesCount ?? null,
        totalDevicesCount: get?.totalDevicesCount ?? null,
        upcomingEventsCount: get?.upcomingEventsCount ?? null,
        activeEvents: (get?.activeEvents ?? null) as DashboardEvent[] | null,
        recentScans: (get?.recentScans ?? null) as DashboardScanLog[] | null,
        alerts: sortAlerts((get?.alerts ?? null) as DashboardAlert[] | null),
        instances: (raw?.instances ?? null) as DashboardInstance[] | null,
    }
}
